import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import NFLPendingClaims from './NFLPendingClaims';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { partitionSlots, slotLabel, draftableSlots, leagueSlotOrder } from '@/lib/nflRosterSlots';
import { usePastWeekLineup } from '@/hooks/usePastWeekLineup';
import { NFLPastWeekLineup } from './NFLPastWeekLineup';
import { fmtCents1, toCents } from '@/lib/nflWeekLineup';
import TeamLogo from './TeamLogo';
import { useTeamLogoUpload, TEAM_LOGO_ACCEPT } from '@/hooks/useTeamLogoUpload';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, TrendingUp, ChevronDown, Zap, ArrowLeftRight, ArrowUpDown, UserMinus, Lock, Clock, Pencil, Check, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useCanPlaySlot } from '@/hooks/useStartablePositions';
import { cn } from '@/lib/utils';
import { AvailabilityPill } from './PlayerAvailability';
import { NFLPlayerMeta, NFLPlayerMetaLine } from './NFLPlayerMeta';
import { shortPlayerName } from '@/lib/nflGameLine';
import { useWeeklyProjections, formatWeeklyProj } from '@/hooks/useWeeklyProjections';
import { useNFLDemoOptional } from '@/contexts/NFLDemoContext';
import NFLWeekSelector from './NFLWeekSelector';
import NFLPlayerDetailModal from './NFLPlayerDetailModal';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Seg } from '@/components/ui/seg';
import NFLTradePage from './NFLTradePage';

interface NFLMyTeamPageProps {
  onNavigate: (tab: string) => void;
  // SAB-512 Phase 2: deep-link into the Trades tab (Trades folded into My Team).
  initialTab?: 'roster' | 'trades';
  /** SAB-1046: pre-target a trade partner when opening straight onto the Trades tab. */
  initialTradeTeamId?: string | null;
  /** SAB-1062: pre-add this player to the partner's side. */
  initialTradePlayerId?: string | null;
  /**
   * SAB-1062: opens the propose-trade flow pre-targeted at a team, optionally with a player
   * pre-added to their side. Threaded from NFLStaging so every surface that can open the
   * player-detail modal can also act on its Trade button -- a surface that cannot supply
   * this simply does not render the button, rather than rendering a dead one.
   */
  onTradeWith?: (teamId: string, playerId?: string) => void;
}

import {
  MY_TEAM_STAT_COLUMNS, NFL_VIEW_OPTIONS, TONE_CLASS, buildStatLine,
  getSabPtsDisplay, getStatDisplay, type NflStatRow, type NflStatView,
} from '@/components/nfl/nflStatsDefs';
import { NFL_RANGE_OPTIONS, rangeLabel, type NflRange } from '@/lib/nflWeekRange';
import { useNflStatRows } from '@/hooks/useNflStatRows';

type StatsFilter = 'total' | 'trash' | 'anti';

/** The score column's heading per toggle state. Tones match the TRS / ANT columns. */
const SCORE_HEAD: Record<StatsFilter, { label: string; tone: string }> = {
  total: { label: 'SAB PTS', tone: 'text-primary' },
  trash: { label: 'TRS', tone: 'text-damage' },
  anti: { label: 'ANT', tone: 'text-success' },
};

/**
 * A player's SAB PTS total for the week: trash + anti + sabotage CARD bonus.
 *
 * SAB-809, third instance. In `nfl_player_match_scores` the column named
 * `sabotage_points` is the sabotage CARD BONUS, NOT the player's total -- the engine
 * writes `sabotage_points: sabBonus` and composes the team score as
 * `trash + anti + sabBonus` (nfl-matchup-engine 708/779). The SAME column name means
 * the player total in `nfl_player_game_stats`, which is how this keeps recurring.
 *
 * Reading the column alone rendered +0.0 for every player who was not sabotaged: on
 * prod's 2026 REG week 1, 1466 of 1471 rows carried 0, while the true total averaged
 * 5.03 and reached 90.4. NFLMatchupDetail made this exact error (its `playerPts` is now
 * the same three-term sum); SAB-525 moved THIS page's read to escape a zero in the other
 * table and inherited this one.
 *
 * The card bonus is a real component, not a rounding detail. Dropping it would be right
 * 1466 times in 1471 and wrong precisely when a sabotage lands -- the case the product
 * exists for.
 *
 * Returns null, never 0, when the player has no score row at all. SAB-809's other lesson:
 * a fabricated 0.0 is indistinguishable from a real zero and sorts as if it were real.
 */
export function weekTotalPoints(p: {
  trash_points: number | null;
  anti_points: number | null;
  sabotage_points: number | null;
}): number | null {
  if (p.trash_points === null && p.anti_points === null && p.sabotage_points === null) return null;
  const sum = (p.trash_points ?? 0) + (p.anti_points ?? 0) + (p.sabotage_points ?? 0);
  return Math.round(sum * 100) / 100;
}

export interface RosterPlayer {
  roster_id: string;
  player_id: string;
  full_name: string;
  position: string;
  nfl_team_abbr: string | null;
  headshot_url: string | null;
  injury_status: string | null;
  status: string;
  /**
   * SAB-1215: may this player occupy an IR slot, and on what authority -- decided by the
   * SERVER (nfl-lineup `ir_authority`, _shared/nfl-ir-authority.ts), never by a client copy.
   * `undefined` means UNKNOWN (the lookup failed): draw no IR button AND raise no banner.
   * Only a known `null` makes an IR occupant illegal.
   */
  irAuthority?: 'official' | 'rotowire_recent' | null;
  /**
   * For a player IN an IR slot: is he entitled to stay? From the server (nfl-lineup
   * `ir_authority` -> _shared/nfl-ir-occupancy.ts), the same decision the legality guard makes.
   * 'illegal' raises the blocking banner; 'needs_review' (a news-backed placement 14 days with
   * no report either way) raises a non-blocking notice. `undefined` = unknown: neither.
   */
  irOccupancy?: { status: 'legal' | 'illegal' | 'needs_review'; reason: string };
  position_slot: string;
  sabotage_points: number | null;
  trash_points: number | null;
  anti_points: number | null;
  // Season stats
  season_stats: Record<string, number> | null;
  // Lock status
  isLocked: boolean;
  lockReason: 'game-started' | 'kickoff-passed' | 'unknown-status' | 'unresolvable-kickoff' | null;
  isBye: boolean;
  kickoff: string | null;
  // SAB-1003: this week's game context, for the "Sun 4:25 PM vs DEN" line.
  opponentAlias: string | null;
  isHome: boolean | null;
  gameStatus: string | null;
  /** nfl_franchises.bye_week_2026 -- the number, for the "RB · KC (5)" suffix. */
  byeWeek: number | null;
  // SAB-994: null = no projection exists; absent = not loaded. Never conflate with 0.
  weeklyProj?: number | null;
}

/**
 * SAB-886: slot rows come from the LEAGUE, not from three hardcoded lists.
 *
 * These were `{slot,label}` constants covering 13 starters, 6 bench and 2 IR. In a league
 * with two FLEX the player the server assigned to FLEX2 had no row here and vanished from
 * their own team page -- the surface a manager checks precisely to see their roster. The
 * same lists rendered permanently-unfillable IDP1/IDP2 rows to leagues with no IDP slot.
 *
 * Labels are derived (`slotLabel`) rather than looked up, so a slot the product has never
 * shipped still renders something rather than a blank cell.
 */

// Position badge colors
const positionColors: Record<string, string> = {
  QB: 'bg-red-500/20 text-red-400 border-red-500/30',
  RB: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  WR: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  TE: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  FLEX: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  FLX: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  K: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  P: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  DST: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  COACH: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  HC: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  IDP: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  LB: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  DL: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  DB: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  BN: 'bg-muted/20 text-muted-foreground border-border',
  IR: 'bg-red-800/20 text-red-300 border-red-800/30',
};

/**
 * What to SAY about a lock.
 *
 * The four tooltips and the swap toast each hardcoded "game started". That was
 * false for three of the four reasons, and on 2026-09-13 it asserted a kickoff
 * that was still two hours away -- which is also why the tooltip could not be
 * used to tell the causes apart while diagnosing. Derived from the reason now.
 */
/**
 * Lineups lock this long BEFORE kickoff, not at it.
 *
 * MUST equal LOCK_BUFFER_MS in supabase/functions/_shared/nflLineupLocks.ts.
 * A client that locks a minute early against a server that locks at kickoff is
 * the same divergence this whole change is fixing, in a 60-second form. The
 * duplication exists only because this file has its own lock copy at all, which
 * is tracked for deletion.
 */
const LOCK_BUFFER_MS = 60_000;

function lockLabel(reason: RosterPlayer['lockReason']): string {
  switch (reason) {
    case 'game-started': return 'Locked: game started';
    case 'kickoff-passed': return 'Locked: kickoff has passed';
    case 'unknown-status': return 'Locked: game status unavailable and kickoff has passed';
    case 'unresolvable-kickoff': return 'Locked: kickoff time unavailable';
    default: return 'Locked';
  }
}

function getSlotColor(slot: string): string {
  const base = slot.replace(/[0-9]/g, '');
  return positionColors[base] || positionColors.BN;
}

/**
 * SAB-1199: shared header-cell styling.
 *
 * Lifted verbatim from the base classes SortHead applied, so removing the sort changed the
 * affordance and nothing else -- same size, weight, casing, tracking and alignment. The
 * hover/focus colour transitions and the active-column highlight are deliberately NOT here:
 * they signalled "this is clickable", which is exactly what is being removed.
 */
const HEAD_CLASS = 'shrink-0 text-right text-[10px] font-bold uppercase tracking-wider tabular-nums';

export default function NFLMyTeamPage({ onNavigate, initialTab = 'roster', initialTradeTeamId, initialTradePlayerId, onTradeWith }: NFLMyTeamPageProps) {
  const { currentLeague, currentTeam, currentWeek, season, teams, isLoading: contextLoading, renameMyTeam } = useNFLLeague();

  // SAB-886: this league's own slots, split the way the page renders them.
  const { STARTER_SLOTS, BENCH_SLOTS, IR_SLOTS } = useMemo(() => {
    const p = partitionSlots(currentLeague?.settings?.roster_slots);
    const withLabels = (slots: string[]) => slots.map((slot) => ({ slot, label: slotLabel(slot) }));
    return {
      STARTER_SLOTS: withLabels(p.starters),
      BENCH_SLOTS: withLabels(p.bench),
      IR_SLOTS: withLabels(p.ir),
    };
  }, [currentLeague?.settings]);
  const { toast } = useToast();
  // SAB-812: eligibility comes from nfl_slot_eligibility, the same table nfl-lineup reads,
  // instead of the hand-maintained NFL_SLOT_POSITION_MAP this file used to import.
  const canPlaySlot = useCanPlaySlot();
  const demoCtx = useNFLDemoOptional();
  const isDemoMode = demoCtx?.isDemoMode ?? false;
  const guardWrite = demoCtx?.guardWrite ?? (() => false);
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  // currentWeek resolves ASYNC (1 first, then the league's week), and the seed above never
  // re-synced -- My Team opened on Week 1 (seen on staging throughout). Harmless while every
  // week rendered the same editable roster; NOT harmless now that a COMPLETED week renders the
  // read-only record: a manager would land on week 1 and could not set this week's lineup.
  // Same fix as NFLMatchupsPage (SAB-501): follow currentWeek until the user picks a week.
  const userPickedWeekRef = useRef(false);
  useEffect(() => {
    if (!userPickedWeekRef.current) setSelectedWeek(currentWeek);
  }, [currentWeek]);
  const handleWeekChange = useCallback((w: number) => {
    userPickedWeekRef.current = true;
    setSelectedWeek(w);
  }, []);
  const [viewTeamId, setViewTeamId] = useState<string | null>(null);
  const [statsFilter, setStatsFilter] = useState<StatsFilter>('total');
  // SAB-1176: RANGE and VIEW. Scoped per surface (the handoff shares them across surfaces in
  // the prototype only). Default This Week / Totals: a manager opens My Team to set THIS
  // week's lineup, and season totals are the wrong number for that. Before kickoff the week is
  // honestly empty (dashes), which is correct -- not a reason to fall back to Season.
  // SAB-1199: no sort state. A lineup is a layout you read, not a leaderboard -- rows render
  // in the league's declared roster_slots order and never move. Sorting lives on Players.
  const [range, setRange] = useState<NflRange>('week');
  const [view, setView] = useState<NflStatView>('total');
  const queryClient = useQueryClient();
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  // SAB-1172: the news badge opens the sheet straight on News.
  const [detailTab, setDetailTab] = useState<'stats' | 'news' | undefined>(undefined);
  const [moveSource, setMoveSource] = useState<RosterPlayer | null>(null);
  const [lineupBusy, setLineupBusy] = useState(false);
  // A lineup mutation refetches the roster IN PLACE via query invalidation. The full-page spinner
  // gates on the roster query's `isPending` (no cached data ⇒ genuine first load) ONLY; a refetch
  // after a swap/move/move_ir/drop keeps the cached roster so isPending stays false and the subtree
  // never remounts (the SAB-814-era bug — proven by NFLMyTeamPage.remount.test.tsx). `pendingSlots`
  // disables just the slots being written so a second move can't fire against stale roster state
  // mid-write, while the rest of the page stays live; it clears when fresh roster data lands.
  const [pendingSlots, setPendingSlots] = useState<string[]>([]);
  // Team-name inline edit — owner-only, mirrors MLBBetaMyTeam (pencil, 24-char cap, save/cancel).
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const handleSaveTeamName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed) {
      toast({ title: 'Team name cannot be empty', variant: 'destructive' });
      return;
    }
    if (trimmed.length > 24) {
      toast({ title: 'Team name must be 24 characters or less', variant: 'destructive' });
      return;
    }
    setIsSavingName(true);
    const success = await renameMyTeam(trimmed);
    setIsSavingName(false);
    if (success) {
      setIsEditingName(false);
      toast({ title: 'Team name updated!' });
    } else {
      toast({ title: 'Failed to update team name', variant: 'destructive' });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
  };

  // SAB-512 Phase 2: Roster/Trades segmented tabs, mirroring MLBBetaMyTeam.
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [pendingTradeCount, setPendingTradeCount] = useState(0);
  // Follow the deep-link tab when the route changes (e.g. an external nav to
  // Trades) without overriding a user's in-page tab switches.
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);
  // Incoming pending trades drive the Trades-tab badge (NFL's "incoming"
  // definition: receiver_team_id = my team, status pending). Realtime-subscribed.
  useEffect(() => {
    if (!currentTeam?.id || !currentLeague?.id) return;
    const fetchPending = async () => {
      const { count } = await supabase
        .from('trades')
        .select('id', { count: 'exact', head: true })
        .eq('league_id', currentLeague.id)
        .eq('receiver_team_id', currentTeam.id)
        .eq('status', 'pending');
      setPendingTradeCount(count || 0);
    };
    fetchPending();
    const channel = supabase
      .channel(`nfl-pending-trades-${currentTeam.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trades', filter: `receiver_team_id=eq.${currentTeam.id}` }, () => { fetchPending(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentTeam?.id, currentLeague?.id]);

  const viewTeam = useMemo(() => {
    if (viewTeamId) return teams.find(t => t.id === viewTeamId) || currentTeam;
    return currentTeam;
  }, [viewTeamId, teams, currentTeam]);

  const isOwnTeam = viewTeam?.id === currentTeam?.id;

  // SAB-1004: the header avatar is the upload affordance for your OWN team only. Another
  // manager's logo is not yours to change -- the storage policy and nfl_teams_update would
  // both refuse it anyway, so offering the control would be a lie.
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { upload: uploadLogo, uploading: uploadingLogo } = useTeamLogoUpload({
    teamId: viewTeam?.id,
    ownerId: viewTeam?.owner_id,
    onDone: () => {
      // The team row is the source of truth for every surface, so refresh the shared query
      // rather than patching local state -- matchups and standings read the same cache.
      queryClient.invalidateQueries({ queryKey: ['nfl-teams', viewTeam?.league_id] });
      toast({ title: 'Logo updated' });
    },
    onError: (message) => toast({ title: 'Logo not saved', description: message, variant: 'destructive' }),
  });

  // Roster + player data + weekly scores for the viewed team/week — the roster cache source
  // (SAB-830). staleTime 0 refetches on tab return in the background (the cached roster stays on
  // screen); a live game (any non-final game this week) gates a 45s poll so scores stay fresh while
  // watched. A lineup mutation invalidates this key rather than flipping a shared loading flag, so
  // the refetch is in-place (isPending stays false) and the subtree never remounts.
  const rosterQuery = useQuery({
    queryKey: ['nfl-roster', currentLeague?.id, viewTeam?.id, selectedWeek, currentWeek],
    enabled: !!viewTeam && !!currentLeague,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (q.state.data?.anyLiveGame ? 45_000 : false),
    queryFn: async (): Promise<{ roster: RosterPlayer[]; anyLiveGame: boolean }> => {
      if (!viewTeam || !currentLeague) return { roster: [], anyLiveGame: false };
      // 1. Get roster assignments with player details
      const { data: rosterData, error: rosterErr } = await supabase
        .from('nfl_rosters')
        .select(`
          id,
          player_id,
          position_slot,
          nfl_players (
            id,
            full_name,
            position,
            jersey_number,
            status,
            injury_status,
            headshot_url,
            nfl_franchise_id
          )
        `)
        .eq('team_id', viewTeam!.id)
        .eq('is_active', true);

      if (rosterErr) throw rosterErr;

      // 1b. SAB-1215: IR authority per player, from the server's one rule. Fetched rather
      // than derived here because the rule has a news arm (SAB-1203) the client must not
      // copy (SAB-1067). A failure leaves every player UNKNOWN, never "ineligible".
      let irAuthorityMap: Record<string, 'official' | 'rotowire_recent' | null> | null = null;
      let irOccupancyMap: Record<string, { status: 'legal' | 'illegal' | 'needs_review'; reason: string }> | null = null;
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess?.session?.access_token;
        if (token) {
          const { data: ira, error: iraErr } = await (supabase.functions.invoke as any)('nfl-lineup', {
            body: { action: 'ir_authority', team_id: viewTeam!.id },
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!iraErr && ira?.ir_authority && typeof ira.ir_authority === 'object') irAuthorityMap = ira.ir_authority;
          if (!iraErr && ira?.ir_occupancy && typeof ira.ir_occupancy === 'object') irOccupancyMap = ira.ir_occupancy;
        }
      } catch { /* unknown, handled below */ }

      // 2. Get weekly scores
      const playerIds = (rosterData || []).map(r => r.player_id).filter(Boolean);
      let scoresMap: Record<string, { sabotage_points: number; trash_points: number; anti_points: number }> = {};

      if (playerIds.length > 0) {
        // SAB-525 (same class as SAB-523): read the per-league source of truth
        // nfl_player_match_scores, keyed on league_id + week + season_year +
        // player_id. The old read hit nfl_player_game_stats, whose sabotage_points
        // is 0 globally (per-league sabotage scoring lives only in match_scores),
        // so My Team rendered 0 SAB PTS for every player. season_year comes from
        // the league (not a hardcoded 2025). Table not in generated types -> cast.
        const { data: scores } = await supabase
          .from('nfl_player_match_scores' as any)
          .select('player_id, sabotage_points, trash_points, anti_points')
          .eq('league_id', currentLeague.id)
          .in('player_id', playerIds)
          .eq('week', selectedWeek)
          .eq('season_year', season)
          // SAB-824: pin the phase. nfl_player_match_scores carries 2026 PRE rows on
          // prod, so week N alone mixes preseason scores into the stats view. REG
          // matches the lock slate below and every other surface on this page.
          .eq('season_type', 'REG');

        if (scores) {
          for (const s of scores) {
            if (!scoresMap[s.player_id]) {
              scoresMap[s.player_id] = { sabotage_points: 0, trash_points: 0, anti_points: 0 };
            }
            scoresMap[s.player_id].sabotage_points += Number(s.sabotage_points) || 0;
            scoresMap[s.player_id].trash_points += Number(s.trash_points) || 0;
            scoresMap[s.player_id].anti_points += Number(s.anti_points) || 0;
          }
        }
      }

      // 3. Get season stats
      let seasonMap: Record<string, Record<string, number>> = {};
      if (playerIds.length > 0) {
        // SAB-525: the column is `stats` (jsonb), not `season` - selecting a
        // nonexistent column returned 400. The table is legitimately empty, so
        // this returns [] and season_stats falls back to null downstream.
        const { data: seasonData } = await supabase
          .from('nfl_player_season_stats')
          .select('player_id, stats')
          .in('player_id', playerIds);

        if (seasonData) {
          for (const ss of seasonData) {
            seasonMap[ss.player_id] = (ss.stats as any) || {};
          }
        }
      }

      // 4. Get franchise abbreviations
      const franchiseIds = (rosterData || [])
        .map(r => (r.nfl_players as any)?.nfl_franchise_id)
        .filter(Boolean);
      let franchiseMap: Record<string, string> = {};
      // SAB-1003: bye week rides on the franchise fetch that already runs for the alias.
      // Embedding `franchise:nfl_franchises ( bye_week_2026 )` on the nfl_rosters select
      // instead tipped the builder into "Invalid Relationships cannot infer result type"
      // and scored +4 on the typecheck ratchet -- this needs no extra query and no cast.
      const franchiseByeMap: Record<string, number | null> = {};

      if (franchiseIds.length > 0) {
        // ALL franchises, not just the roster's. This map also resolves the OPPONENT alias
        // (step 5), and an opponent is almost never on the manager's own roster -- filtering
        // to roster franchises left most rows reading "Sun 1:00 PM" with no opponent at all,
        // from SAB-1003 until this. It is 32 rows; the filter saved nothing worth having.
        const { data: franchises } = await supabase
          .from('nfl_franchises')
          .select('id, alias, bye_week_2026');

        if (franchises) {
          // Typed at the loop rather than the select: adding a third column tips the
          // builder's conditional types into a SelectQueryError union (the same TS2589-class
          // hazard the nfl_games reads document), and destructuring off that scores on the
          // ratchet. One cast here keeps the query untouched and the ratchet at baseline.
          for (const f of franchises as unknown as { id: string; alias: string | null; bye_week_2026: number | null }[]) {
            franchiseMap[f.id] = f.alias || '';
            franchiseByeMap[f.id] = f.bye_week_2026 ?? null;
          }
        }
      }

      // 5. Get game lock status — find games for each franchise this week
      let franchiseGameMap: Record<string, { scheduled: string; status: string; opponentId: string | null; isHome: boolean }> = {};
      let anyLiveGame = false;
      if (franchiseIds.length > 0) {
        // SAB-823: mirror the server lock slate (nfl-lineup `checkLocks`) EXACTLY. The
        // client had diverged from checkLocks twice: (1) season_year was hardcoded to 2025,
        // so a 2026 league read a *completed* season's games and marked every player locked
        // (final/past) — this rendered every player locked in every NFL league on prod;
        // (2) no season_type filter, so once the year was right PRE and REG week-N would
        // collide (the SAB-746 class). The move/swap/move_ir calls below all target
        // `currentWeek` REG, so the displayed lock must resolve against that same slate —
        // the league's own season_year, season_type REG, currentWeek — NOT selectedWeek
        // (the stats view week). checkLocks is the source of truth; see nflSlots.test.ts
        // for the drift-guard discipline this mirrors.
        // `as any`: nfl_games is in the generated types, and a third chained .eq() tips the
        // Supabase builder's conditional types into TS2589 (excessively deep). Same escape
        // hatch the NFL tables use elsewhere (e.g. NFLLeagueContext's `.from('nfl_teams' as any)`).
        const { data: weekGames } = await (supabase
          .from('nfl_games' as any)
          .select('home_franchise_id, away_franchise_id, scheduled, status')
          .eq('season_year', season)
          .eq('season_type', 'REG')
          .eq('week', currentWeek) as any);

        if (weekGames) {
          for (const g of weekGames) {
            // SAB-1003: keep the OTHER side. The map already saw both franchise ids and
            // discarded one, which is why no NFL surface has ever shown an opponent. Storing
            // it costs nothing and needs no extra query.
            franchiseGameMap[g.home_franchise_id] = {
              scheduled: g.scheduled, status: g.status,
              opponentId: g.away_franchise_id, isHome: true,
            };
            franchiseGameMap[g.away_franchise_id] = {
              scheduled: g.scheduled, status: g.status,
              opponentId: g.home_franchise_id, isHome: false,
            };
          }
          // A live game is any this-week game that has left 'scheduled' but not yet finalised —
          // gates the 45s poll (same non-final heuristic as the Matchups page).
          anyLiveGame = (weekGames as any[]).some((g: any) => {
            const s = String(g.status || '').toLowerCase();
            return s !== 'scheduled' && s !== 'closed' && s !== 'complete' && s !== 'completed' && s !== 'final' && s !== 'cancelled' && s !== 'postponed';
          });
        }
      }

      const now = new Date();

      const built: RosterPlayer[] = (rosterData || []).map(r => {
        const p = r.nfl_players as any;
        const score = scoresMap[r.player_id] || null;
        const franchiseId = p?.nfl_franchise_id;
        const game = franchiseId ? franchiseGameMap[franchiseId] : null;

        let isLocked = false;
        /** Why, for the tooltip. Null when unlocked. */
        let lockReason: 'game-started' | 'kickoff-passed' | 'unknown-status' | 'unresolvable-kickoff' | null = null;
        let isBye = false;
        let kickoff: string | null = null;

        let opponentAlias: string | null = null;
        let isHome: boolean | null = null;

        if (!game) {
          isBye = true;
        } else {
          kickoff = game.scheduled;
          // SAB-1003: opponent resolved through the same franchiseMap the player's own abbr
          // uses, so "vs DEN" and the team chip can never disagree about an alias.
          opponentAlias = game.opponentId ? (franchiseMap[game.opponentId] || null) : null;
          isHome = game.isHome;
          // SAB-1122, CLIENT HALF. The server got this on Thursday
          // (_shared/nflLineupLocks.ts); this file has its own copy and did not,
          // so on 2026-09-13 thirteen week-1 games sat at 'unknown' with kickoffs
          // hours away and ~1,438 roster spots across 82 teams read as locked.
          //
          // The predicate was `status !== 'scheduled' || kickoff <= now`, which
          // locks ANY unmappable status regardless of the clock. An unmappable
          // status is not evidence a game started; the kickoff timestamp is
          // independent and strictly stronger, because a game cannot begin before
          // its own kickoff.
          //
          // Order and vocabulary mirror _shared/nflLineupLocks.ts deliberately.
          // The duplication is the root cause and is tracked for deletion; this is
          // the minimal change under a two-hour clock, not the fix.
          const kickoffMs = Date.parse(game.scheduled);
          const statusLower = String(game.status ?? '').trim().toLowerCase();
          // Mirrors NFL_STARTED_STATUSES. postponed/cancelled lock DELIBERATELY:
          // a postponed game is replayed later and a cancelled one scores zero, and
          // letting an owner swap out after the fact is the manipulation this guard
          // exists to prevent.
          const startedStatus = ['in-progress', 'final', 'delayed', 'postponed', 'cancelled']
            .includes(statusLower);
          const notStartedStatus = statusLower === 'scheduled';

          if (Number.isNaN(kickoffMs)) {
            // Neither signal. Fail closed. Losing this branch would unlock every
            // game with a missing kickoff.
            isLocked = true;
            lockReason = 'unresolvable-kickoff';
          } else if (startedStatus) {
            isLocked = true;
            lockReason = 'game-started';
          } else if (kickoffMs - LOCK_BUFFER_MS <= now.getTime()) {
            isLocked = true;
            lockReason = notStartedStatus ? 'kickoff-passed' : 'unknown-status';
          } else if (notStartedStatus) {
            isLocked = false;
          } else {
            // Unmappable status, kickoff still ahead -> EDITABLE. The whole fix.
            isLocked = false;
          }
        }

        return {
          roster_id: r.id,
          player_id: r.player_id,
          full_name: p?.full_name || 'Unknown',
          position: p?.position || '??',
          nfl_team_abbr: franchiseId ? (franchiseMap[franchiseId] || null) : null,
          headshot_url: p?.headshot_url || null,
          injury_status: p?.injury_status || null,
          status: p?.status || 'ACT',
          irAuthority: irAuthorityMap ? (irAuthorityMap[r.player_id] ?? null) : undefined,
          irOccupancy: irOccupancyMap ? irOccupancyMap[r.player_id] : undefined,
          opponentAlias,
          isHome,
          gameStatus: game?.status ?? null,
          byeWeek: franchiseId ? (franchiseByeMap[franchiseId] ?? null) : null,
          position_slot: r.position_slot,
          sabotage_points: score?.sabotage_points ?? null,
          trash_points: score?.trash_points ?? null,
          anti_points: score?.anti_points ?? null,
          season_stats: seasonMap[r.player_id] || null,
          isLocked,
          lockReason,
          isBye,
          kickoff,
        };
      });

      return { roster: built, anyLiveGame };
    },
  });
  const rosterRaw = rosterQuery.data?.roster ?? [];

  /**
   * SAB-994: this week's projections for every rostered player.
   *
   * Keyed on currentWeek, not selectedWeek. selectedWeek drives the STATS view (a manager
   * can look back at week 3's actuals), but a projection for a week already played is not
   * a projection -- it would sit beside real points implying we still expect them.
   */
  const weeklyProjMap = useWeeklyProjections(
    useMemo(() => rosterRaw.map(r => r.player_id), [rosterRaw]),
    currentWeek,
  );
  const roster = useMemo(
    () => rosterRaw.map(r => ({ ...r, weeklyProj: weeklyProjMap.get(r.player_id) })),
    [rosterRaw, weeklyProjMap],
  );
  const isLoading = rosterQuery.isPending;
  // Clear pendingSlots when fresh roster data lands — the written slots are now up to date and
  // re-enabled. Mirrors the old fetch-finale clear; dataUpdatedAt advances on every successful fetch.
  useEffect(() => { setPendingSlots([]); }, [rosterQuery.dataUpdatedAt]);

  const slotMap = useMemo(() => {
    const map: Record<string, RosterPlayer> = {};
    for (const p of roster) map[p.position_slot] = p;
    return map;
  }, [roster]);

  const refetchRoster = () => { queryClient.invalidateQueries({ queryKey: ['nfl-roster'] }); };

  // SAB-814 — owner-only lineup actions via the hardened nfl-lineup / nfl-add-drop routes.
  // JWT-authed invoke, mirroring MLB's invokeDailyLineup. The client has never called nfl-lineup
  // before this; the server (SAB-751) is the fail-closed authority — this is a second layer.
  const invokeEdge = async (name: string, body: Record<string, any>): Promise<{ ok: boolean; error: string | null }> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) return { ok: false, error: 'No session. Please log in again.' };
    const { data, error, response } = await (supabase.functions.invoke as any)(name, {
      body,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error) {
      let detail = error.message || 'Request failed';
      try {
        const res = (error as any).context || response;
        if (res && typeof res.json === 'function') { const b = await res.json(); detail = b?.error || b?.message || detail; }
      } catch { /* body already consumed */ }
      return { ok: false, error: detail };
    }
    if (data?.error) return { ok: false, error: data.error };
    return { ok: true, error: null };
  };

  const handleDrop = async (player: RosterPlayer) => {
    if (isDemoMode) { guardWrite(); return; }
    if (!currentTeam || lineupBusy) return;
    if (player.isLocked) { toast({ title: 'Locked', description: `${player.full_name}: ${lockLabel(player.lockReason).replace(/^Locked: /, '')}.`, variant: 'destructive' }); return; }
    if (!window.confirm(`Drop ${player.full_name}? This frees the roster spot and cannot be undone.`)) return;
    setPendingSlots([player.position_slot]);
    setLineupBusy(true);
    const { ok, error } = await invokeEdge('nfl-add-drop', { action: 'drop', team_id: currentTeam.id, player_id: player.player_id });
    setLineupBusy(false);
    if (!ok) { setPendingSlots([]); toast({ title: 'Drop failed', description: error || undefined, variant: 'destructive' }); return; }
    toast({ title: `Dropped ${player.full_name}` });
    setMoveSource(null);
    refetchRoster();
  };

  // SAB-1064: move a RECOVERED player out of IR. This is the first client caller of the
  // server's `activate` action — which existed but nothing invoked, so until now the UI had
  // no way out of an IR slot at all: moveTargetSlots excludes IR sources, and handleMoveIr
  // only moves players IN. That absence is why an illegal roster was a trap rather than an
  // inconvenience, and it is why the banner below is a remedy and not just a notice.
  const handleActivateFromIr = async (player: RosterPlayer) => {
    if (isDemoMode) { guardWrite(); return; }
    if (!currentTeam || lineupBusy) return;
    if (player.isLocked) { toast({ title: 'Locked', description: `${player.full_name}: ${lockLabel(player.lockReason).replace(/^Locked: /, '')}.`, variant: 'destructive' }); return; }
    setPendingSlots([player.position_slot]);
    setLineupBusy(true);
    const { ok, error } = await invokeEdge('nfl-lineup', { action: 'activate', team_id: currentTeam.id, week: currentWeek, player_id: player.player_id });
    setLineupBusy(false);
    if (!ok) {
      setPendingSlots([]);
      // The server refuses when there is no open slot — a real state, and the manager has to
      // choose the drop victim himself. Auto-picking one is not ours to do (SAB-1064).
      toast({ title: 'Could not move out of IR', description: error || undefined, variant: 'destructive' });
      return;
    }
    toast({ title: `${player.full_name} activated` });
    setMoveSource(null);
    refetchRoster();
  };

  const handleMoveIr = async (player: RosterPlayer) => {
    if (isDemoMode) { guardWrite(); return; }
    if (!currentTeam || lineupBusy) return;
    if (player.isLocked) { toast({ title: 'Locked', description: `${player.full_name}: ${lockLabel(player.lockReason).replace(/^Locked: /, '')}.`, variant: 'destructive' }); return; }
    setPendingSlots([player.position_slot]);
    setLineupBusy(true);
    const { ok, error } = await invokeEdge('nfl-lineup', { action: 'move_ir', team_id: currentTeam.id, week: currentWeek, player_id: player.player_id });
    setLineupBusy(false);
    if (!ok) { setPendingSlots([]); toast({ title: 'Could not move to IR', description: error || undefined, variant: 'destructive' }); return; }
    toast({ title: `${player.full_name} → IR` });
    setMoveSource(null);
    refetchRoster();
  };

  // Select-mode: tap a player's Swap → it becomes the source; empty eligible slots light up as
  // targets (swap-onto-a-player is added last). Tapping an empty eligible slot MOVES the source.
  const handleStartMove = (player: RosterPlayer) => {
    if (isDemoMode) { guardWrite(); return; }
    if (player.isLocked) { toast({ title: 'Locked', description: `${player.full_name}: ${lockLabel(player.lockReason).replace(/^Locked: /, '')}.`, variant: 'destructive' }); return; }
    setMoveSource(prev => (prev?.roster_id === player.roster_id ? null : player));
  };
  const handleCancelMove = () => setMoveSource(null);

  const handleMoveToSlot = async (slot: string) => {
    if (!moveSource || !currentTeam || lineupBusy) return;
    // Client pre-check reads the SAME TABLE as the server (SAB-812), not a copy of it;
    // the server re-validates and is the fail-closed authority.
    if (!canPlaySlot(moveSource.position, slot)) {
      toast({ title: 'Not eligible', description: `${moveSource.full_name} (${moveSource.position}) can't play ${slot}.`, variant: 'destructive' });
      return;
    }
    setPendingSlots([moveSource.position_slot, slot]);
    setLineupBusy(true);
    const { ok, error } = await invokeEdge('nfl-lineup', { action: 'move', team_id: currentTeam.id, week: currentWeek, player_id: moveSource.player_id, target_slot: slot });
    setLineupBusy(false);
    if (!ok) { setPendingSlots([]); toast({ title: 'Move failed', description: error || undefined, variant: 'destructive' }); return; }
    toast({ title: `${moveSource.full_name} → ${slot}` });
    setMoveSource(null);
    refetchRoster();
  };

  // Which EMPTY slots the current move-source may drop into (move); IR excluded (use move_ir).
  const moveTargetSlots = useMemo(() => {
    const s = new Set<string>();
    if (!moveSource) return s;
    for (const { slot } of [...STARTER_SLOTS, ...BENCH_SLOTS]) {
      if (slotMap[slot]) continue;                       // only empty slots
      if (slot === moveSource.position_slot) continue;
      if (canPlaySlot(moveSource.position, slot)) s.add(slot);
    }
    return s;
  }, [moveSource, slotMap, canPlaySlot]);

  // SAB-1064: IR slots holding a RECOVERED player. SAB-1215: read from the server's
  // irAuthority -- the same resolveIrAuthorities _shared/nfl-ir-legality.ts uses -- so the
  // banner and the 409 cannot disagree about who the problem is. Before, this read
  // isIREligible alone and would have named a player the manager had just been allowed to
  // place on IR on a RotoWire report. UNKNOWN authority (lookup failed) is never "illegal".
  // Non-blocking: news-backed IR placements 14 days old with no team report either way.
  const reviewIrOccupants = useMemo(
    () => Object.entries(slotMap)
      .filter(([slot, occ]) => /^IR[0-9]*$/i.test(slot) && occ?.irOccupancy?.status === 'needs_review')
      .map(([, occ]) => occ as RosterPlayer),
    [slotMap],
  );

  const illegalIrOccupants = useMemo(() => {
    const out: RosterPlayer[] = [];
    for (const [slot, occ] of Object.entries(slotMap)) {
      if (!/^IR[0-9]*$/i.test(slot) || !occ) continue;
      // IR occupancy follow-up: the SERVER's occupancy verdict, not "no authority". A news-backed
      // placement stays legal after its flag ages out until his team reports without him.
      if (occ.irOccupancy?.status !== 'illegal') continue;
      out.push(occ);
    }
    return out;
  }, [slotMap]);

  // Which OCCUPIED slots the move-source may SWAP with: eligible BOTH ways, occupant not locked,
  // neither side IR (IR moves go through move_ir). Client pre-check; server is the authority.
  const swapTargetSlots = useMemo(() => {
    const s = new Set<string>();
    if (!moveSource || moveSource.position_slot.startsWith('IR')) return s;
    for (const [slot, occ] of Object.entries(slotMap)) {
      if (!occ || occ.roster_id === moveSource.roster_id) continue;
      if (occ.isLocked || slot.startsWith('IR')) continue;
      if (canPlaySlot(moveSource.position, slot) && canPlaySlot(occ.position, moveSource.position_slot)) s.add(slot);
    }
    return s;
  }, [moveSource, slotMap, canPlaySlot]);

  // Occupied slots the source COULD start in, but the occupant can't take the source's (dedicated)
  // slot — so a reciprocal swap is invalid even though a legal lineup IS reachable by benching the
  // occupant first (the FLEX cross-position case: e.g. TE-starter → FLEX holding a WR). These are
  // NOT dimmed like an ordinary ineligible slot; they render blocked-with-a-reason and, on tap,
  // explain the two-step. Mutually exclusive with swapTargetSlots (which requires BOTH directions).
  const blockedSwapSlots = useMemo(() => {
    const s = new Set<string>();
    if (!moveSource || moveSource.position_slot.startsWith('IR')) return s;
    for (const [slot, occ] of Object.entries(slotMap)) {
      if (!occ || occ.roster_id === moveSource.roster_id) continue;
      if (occ.isLocked || slot.startsWith('IR')) continue;
      if (canPlaySlot(moveSource.position, slot) && !canPlaySlot(occ.position, moveSource.position_slot)) s.add(slot);
    }
    return s;
  }, [moveSource, slotMap, canPlaySlot]);

  // Tapping a blocked slot explains the two-step instead of silently doing nothing. No write fires.
  const handleBlockedSwap = (slot: string) => {
    if (!moveSource) return;
    const occ = slotMap[slot];
    if (!occ) return;
    toast({
      title: `Bench ${occ.full_name} first`,
      description: `${occ.full_name} (${occ.position}) can't move to your ${moveSource.position_slot} slot. Bench them to free ${slot}, then move ${moveSource.full_name} in.`,
    });
  };

  const handleSwap = async (target: RosterPlayer) => {
    if (!moveSource || !currentTeam || lineupBusy) return;
    if (target.roster_id === moveSource.roster_id) { setMoveSource(null); return; }
    if (moveSource.isLocked || target.isLocked) { toast({ title: 'Locked', description: `${(moveSource.isLocked ? moveSource : target).full_name}: ${lockLabel((moveSource.isLocked ? moveSource : target).lockReason).replace(/^Locked: /, '')}.`, variant: 'destructive' }); return; }
    if (!canPlaySlot(moveSource.position, target.position_slot) || !canPlaySlot(target.position, moveSource.position_slot)) {
      toast({ title: 'Not eligible', description: `${moveSource.full_name} (${moveSource.position}) can't play ${target.position_slot}, or ${target.full_name} (${target.position}) can't play ${moveSource.position_slot}.`, variant: 'destructive' });
      return;
    }
    setPendingSlots([moveSource.position_slot, target.position_slot]);
    setLineupBusy(true);
    const { ok, error } = await invokeEdge('nfl-lineup', { action: 'swap', team_id: currentTeam.id, week: currentWeek, player1_id: moveSource.player_id, player2_id: target.player_id });
    setLineupBusy(false);
    if (!ok) { setPendingSlots([]); toast({ title: 'Swap failed', description: error || undefined, variant: 'destructive' }); return; }
    toast({ title: `Swapped ${moveSource.full_name} ↔ ${target.full_name}` });
    setMoveSource(null);
    refetchRoster();
  };

  // ── SAB-1176: the stat read ────────────────────────────────────────────────
  // Two sources by design (see useNflStatRows): SAB/TRS/ANT from
  // nfl_player_match_scores -- the league's own numbers, card bonus included, matching the
  // week header above -- and the stat CATEGORIES from nfl_player_game_stats, same weeks.
  // No single table carries both. Do not collapse them.
  const rosterPlayerIds = useMemo(() => roster.map(r => r.player_id).filter(Boolean), [roster]);
  const { rows: statRows, isEmptyRange, emptyReason, isPending: statsPending } = useNflStatRows({
    leagueId: currentLeague?.id ?? null,
    seasonYear: season,
    currentWeek,
    range,
    playerIds: rosterPlayerIds.length ? rosterPlayerIds : null,
    enabled: rosterPlayerIds.length > 0,
  });

  const statRowFor = useCallback(
    (p: RosterPlayer | null): NflStatRow | null => (p ? statRows.get(p.player_id) ?? null : null),
    [statRows],
  );

  const weekTotal = useMemo(() => {
    let total = 0, trash = 0, anti = 0;
    for (const { slot } of STARTER_SLOTS) {
      const p = slotMap[slot];
      if (p) {
        // SAB-809: the three-term sum, not the bare card-bonus column. See weekTotalPoints.
        total += weekTotalPoints(p) ?? 0;
        trash += p.trash_points ?? 0;
        anti += p.anti_points ?? 0;
      }
    }
    return { total, trash, anti };
  }, [slotMap]);

  /**
   * A COMPLETED week is shown from the record, not from today's roster (lib/nflWeekLineup.ts).
   * `weekTotal` above sums the CURRENT starter slots, which for a past week is the bug: it
   * summed whoever is starting now against that week's scores. For a completed week the
   * score card shows the STORED total instead, and the editable tables give way to the
   * read-only lineup as it was scored.
   */
  const fullSlotOrder = useMemo(
    () => leagueSlotOrder(currentLeague?.settings?.roster_slots),
    [currentLeague?.settings],
  );
  const pastWeekQuery = usePastWeekLineup({
    leagueId: currentLeague?.id,
    teamId: viewTeam?.id,
    week: selectedWeek,
    seasonYear: season,
    slotOrder: fullSlotOrder,
    enabled: selectedWeek !== currentWeek,
  });
  const pastWeek = selectedWeek !== currentWeek && pastWeekQuery.data?.completed ? pastWeekQuery.data : null;
  const pastWeekTotals = useMemo(() => {
    if (!pastWeek) return null;
    const l = pastWeek.lineup;
    const counted = l && l.status === 'recorded' ? [...l.slots.flatMap((x) => (x.row ? [x.row] : [])), ...l.alsoCounted] : [];
    return {
      total: pastWeek.storedTotal,
      trashCents: counted.reduce((a, r) => a + toCents(r.trash_points), 0),
      antiCents: counted.reduce((a, r) => a + toCents(r.anti_points), 0),
    };
  }, [pastWeek]);

  /**
   * SAB-994: projected total for the STARTING lineup, which is what the ticket asks for and
   * what changes as a manager swaps players.
   *
   * `proj` is null when NOTHING in the lineup has a projection -- rendering 0.0 there would
   * claim we expect zero points from a full roster. `counted` lets the UI say how much of
   * the lineup the number actually covers, so a partial sum is never mistaken for a total.
   */
  const weekProjTotal = useMemo(() => {
    let sum = 0, counted = 0;
    for (const { slot } of STARTER_SLOTS) {
      const v = slotMap[slot]?.weeklyProj;
      if (v !== null && v !== undefined) { sum += v; counted++; }
    }
    return { proj: counted > 0 ? sum : null, counted, of: STARTER_SLOTS.length };
  }, [slotMap]);

  // SAB-986 (reopened): IR is a ghost slot -- storage for an injured player, not active
  // roster capacity -- so it counts toward neither side of this figure. The header read
  // "17/18" post-draft for League of Trash: 18 is every slot, but IR1 is not capacity, and
  // no legal roster could ever reach it.
  //
  // Routed through the shared draftableSlots() so this surface and the two draft-room
  // headers cannot drift on what "counts". The IR block below keeps its own separate
  // count, which is the right place to show IR occupancy.
  //
  // BOTH sides move. `roster.length` was a raw player count, so a player legitimately on
  // IR -- normal post-draft, unlike in the draft room -- would have printed 18/17 against
  // the new denominator. Counting occupied ACTIVE slots keeps numerator and denominator on
  // one slot set.
  const activeSlots = useMemo(
    () => draftableSlots([...STARTER_SLOTS, ...BENCH_SLOTS, ...IR_SLOTS].map(s => s.slot)),
    [STARTER_SLOTS, BENCH_SLOTS, IR_SLOTS],
  );
  const rosterCount = activeSlots.filter(s => slotMap[s]).length;
  const totalSlots = activeSlots.length;

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague || !currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Users className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to see your team</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  function getScore(player: RosterPlayer): number | null {
    if (statsFilter === 'trash') return player.trash_points;
    if (statsFilter === 'anti') return player.anti_points;
    // TOT is the player's TOTAL, which `sabotage_points` alone is not (SAB-809).
    return weekTotalPoints(player);
  }

  function formatScore(val: number | null): string {
    if (val === null || val === undefined) return '--';
    return val >= 0 ? `+${val.toFixed(1)}` : val.toFixed(1);
  }

  // SAB-814 (mobile): the RosterPlayer behind the open detail modal, if it is one of the viewer's
  // own roster players — lets the modal host the same move/IR/drop actions the desktop RosterRow
  // buttons do (which are hidden on mobile). irEligible mirrors the RosterRow rule verbatim.
  const detailRosterPlayer = detailPlayerId ? roster.find(p => p.player_id === detailPlayerId) ?? null : null;
  // SAB-965: shared rule. Read `injury_status` alone, this list made NO player eligible --
  // the column is NULL for all 3,328 -- so a manager who drafted someone on IR could not
  // stash him. It also admitted Doubtful, a week-to-week game status.
  const detailIrEligible = !!detailRosterPlayer
    && !detailRosterPlayer.position_slot.startsWith('IR')
    && !!detailRosterPlayer.irAuthority;

  return (
    <ActiveNewsProvider sport="nfl" keys={roster.map(r => r.player_id)}>
    <div className="space-y-4">
      {/* SAB-512 Phase 2: Roster / Trades segmented tabs (mirrors MLBBetaMyTeam:
          Seg on mobile, TabsList on desktop, pendingTradeCount badge on Trades). */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="sm:hidden">
          <Seg
            ariaLabel="Roster or trades"
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'roster', label: (<><Users className="w-3.5 h-3.5" /><span>Roster</span></>) },
              { value: 'trades', label: (
                <>
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  <span>Trades</span>
                  {pendingTradeCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center font-bold">{pendingTradeCount}</span>
                  )}
                </>
              ) },
            ]}
          />
        </div>
        <TabsList className="hidden sm:flex w-full p-1 rounded-md">
          <TabsTrigger value="roster" className="flex-1 gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Roster
          </TabsTrigger>
          <TabsTrigger value="trades" className="flex-1 gap-1.5 relative">
            <ArrowLeftRight className="w-3.5 h-3.5" />
            Trades
            {pendingTradeCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center font-bold">{pendingTradeCount}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="space-y-4 mt-4">
      {/* Team Header */}
      <div className="flex flex-col gap-3">
        {/* Row 1: Team identity */}
        <div className="flex items-center gap-3 min-w-0">
          {isOwnTeam ? (
            <>
              <button
                type="button"
                data-testid="team-logo-upload"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                aria-label="Change team logo"
                title="Change team logo"
                className="relative shrink-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
              >
                <TeamLogo
                  logoUrl={viewTeam?.logo_url}
                  name={viewTeam?.name}
                  rounded="xl"
                  className="h-11 w-11"
                  textClassName="text-lg"
                />
                <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/55 text-[9px] font-bold uppercase tracking-wide text-white opacity-0 transition-opacity hover:opacity-100">
                  {uploadingLogo ? '…' : 'Edit'}
                </span>
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept={TEAM_LOGO_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // Reset the input so re-picking the SAME file fires change again.
                  e.target.value = '';
                  if (f) void uploadLogo(f);
                }}
              />
            </>
          ) : (
            <TeamLogo
              logoUrl={viewTeam?.logo_url}
              name={viewTeam?.name}
              rounded="xl"
              className="h-11 w-11"
              textClassName="text-lg"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-1 min-w-0">
                {isEditingName && isOwnTeam ? (
                  <>
                    <Input
                      value={editNameValue}
                      onChange={(e) => setEditNameValue(e.target.value.slice(0, 24))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTeamName();
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      maxLength={24}
                      className="h-8 w-48 text-sm font-bold"
                      autoFocus
                      disabled={isSavingName}
                    />
                    <span className="text-xs text-muted-foreground">{editNameValue.length}/24</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleSaveTeamName} disabled={isSavingName}>
                      <Check className="w-3.5 h-3.5 text-primary" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancelEdit} disabled={isSavingName}>
                      <X className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                      className="flex items-center gap-1 text-foreground font-display font-bold text-lg hover:text-primary transition-colors min-w-0"
                    >
                      <span className="truncate">{viewTeam?.name || 'My Team'}</span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                    {isOwnTeam && (
                      <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => { setEditNameValue(viewTeam?.name || ''); setIsEditingName(true); }}>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0 ml-2">
                {viewTeam?.wins ?? 0}-{viewTeam?.losses ?? 0}{(viewTeam?.ties ?? 0) > 0 ? `-${viewTeam?.ties}` : ''}
              </Badge>

              {showTeamDropdown && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-lg shadow-xl z-50 py-1 max-h-64 overflow-y-auto">
                  {teams.map(t => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setViewTeamId(t.id === currentTeam.id ? null : t.id);
                        setShowTeamDropdown(false);
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm transition-colors',
                        t.id === viewTeam?.id
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-muted/50'
                      )}
                    >
                      <span className="truncate">{t.name}</span>
                      {t.id === currentTeam.id && (
                        <span className="text-[10px] text-muted-foreground ml-2">you</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Resource chips */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar text-xs text-muted-foreground">
          <span className="shrink-0">{rosterCount}/{totalSlots} roster</span>
          {isOwnTeam && (
            <>
              <span className="text-border shrink-0">|</span>
              <span className="flex items-center gap-1 shrink-0">
                <Zap className="h-3 w-3 text-damage" />
                <span className="text-damage font-medium">{Number((viewTeam as any)?.sabotage_balance) || 100} SP</span>
              </span>
              <span className="text-border shrink-0">|</span>
              <span className="text-emerald-400 font-medium shrink-0">${Number((viewTeam as any)?.faab_budget) || 0} FAAB</span>
            </>
          )}
        </div>

        {/* Row 3: Controls */}
        <div className="flex items-center justify-between gap-2">
          <NFLWeekSelector
            currentWeek={currentWeek}
            selectedWeek={selectedWeek}
            onWeekChange={handleWeekChange}
          />
          <div className="flex rounded-lg border border-border overflow-hidden shrink-0">
            {(['total', 'trash', 'anti'] as StatsFilter[]).map(f => (
              <button
                key={f}
                onClick={() => setStatsFilter(f)}
                className={cn(
                  'px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors min-h-[44px]',
                  statsFilter === f
                    ? f === 'trash' ? 'bg-damage/10 text-damage'
                    : f === 'anti' ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {f === 'total' ? 'TOT' : f === 'trash' ? 'TRS' : 'ANT'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Week Score Summary */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Week {selectedWeek} Score</p>
              {(() => {
                // Completed week: the STORED total (what standings use). Live week: unchanged.
                const t = pastWeekTotals ? (pastWeekTotals.total ?? 0) : weekTotal.total;
                return (
                  <p data-testid="week-score" className={cn(
                    'text-2xl font-bold tabular-nums',
                    t > 0 ? 'text-damage' : t < 0 ? 'text-emerald-400' : 'text-muted-foreground'
                  )}>
                    {pastWeekTotals
                      ? (pastWeekTotals.total === null ? '--' : fmtCents1(Math.round(pastWeekTotals.total * 100)))
                      : `${weekTotal.total >= 0 ? '+' : ''}${weekTotal.total.toFixed(1)}`}
                  </p>
                );
              })()}
            </div>
            <div className="flex gap-4 text-right">
              {/* SAB-994: projected starting-lineup total, shown only for the CURRENT week.
                  For a week already played the actual is the answer and a projection beside
                  it would invite "we still expect this". Hidden entirely when nothing in the
                  lineup carries a projection, rather than printing 0.0 for a full roster. */}
              {selectedWeek === currentWeek && weekProjTotal.proj !== null && (
                <div>
                  <p className="text-[10px] text-muted-foreground">
                    Proj{weekProjTotal.counted < weekProjTotal.of ? ` (${weekProjTotal.counted}/${weekProjTotal.of})` : ''}
                  </p>
                  <p className="text-sm font-bold text-muted-foreground tabular-nums">
                    {weekProjTotal.proj.toFixed(1)}
                  </p>
                </div>
              )}
              <div>
                <p className="text-[10px] text-muted-foreground">Trash</p>
                <p className="text-sm font-bold text-damage tabular-nums">{pastWeekTotals ? fmtCents1(pastWeekTotals.trashCents) : `+${weekTotal.trash.toFixed(1)}`}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Anti</p>
                <p className="text-sm font-bold text-emerald-400 tabular-nums">{pastWeekTotals ? fmtCents1(pastWeekTotals.antiCents) : weekTotal.anti.toFixed(1)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SAB-1176: RANGE + VIEW.
          RANGE is week-based, not MLB's rolling day window -- football is played in weeks
          (see nflWeekRange). All three options always render: a control that appears
          mid-season is worse than an honest empty state, and the empty state is needed for
          byes and lookbacks regardless. */}
      <Card className="bg-card border-border">
        <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
          <LabeledSeg
            label="Range"
            options={NFL_RANGE_OPTIONS}
            value={range}
            onChange={(v) => setRange(v as NflRange)}
          />
          <LabeledSeg
            label="View"
            options={NFL_VIEW_OPTIONS}
            value={view}
            onChange={(v) => setView(v as NflStatView)}
          />
        </CardContent>
      </Card>

      {isEmptyRange && (
        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          {emptyReason === 'no-prior-week'
            ? 'No previous week yet — this is week 1.'
            : 'No week is in play for this league yet.'}
        </div>
      )}

      {/* SAB-1017: a manager's live bids, with cancel. Only rendered on their OWN team --
          another manager's pending claims are blind-bid information and showing them would
          break the blind. The component returns null when there is nothing pending.

          SAB-1142b: moved from the top of the roster tab to here, between the week score
          card and Starters. It sat above the team header, before the manager had seen how
          the week is going -- which is the context that makes a pending bid mean anything.
          It now reads in the order the decision is actually made: this is my score, these
          are the claims I have working, this is my lineup. */}
      {isOwnTeam && (
        <NFLPendingClaims
          teamId={currentTeam?.id ?? null}
          /* Already loaded — the panel adds no query of its own. Undefined rather than a
             default when the team is absent: the panel omits its meter instead of showing
             a budget nobody holds. */
          faabRemaining={currentTeam ? Number(currentTeam.faab_budget) : undefined}
        />
      )}

      {/*
        SAB-1064: PERSISTENT while the roster is IR-illegal — not a toast, not a surprise 400.
        An IR slot is extra capacity granted on condition its occupant cannot play; once he
        recovers the roster is over the limit and every add, claim and trade is refused. The
        manager needs to know that before he tries something, and needs the fix in reach.
      */}
      {reviewIrOccupants.length > 0 && (
        <div data-testid="ir-needs-review" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
          <span className="font-semibold">IR placement needs review</span>
          {' — '}
          {reviewIrOccupants.map(p => `${p.full_name} (${p.position_slot})`).join(', ')}.
          {' '}Placed on IR from a RotoWire injury report 14+ days ago, and his team hasn't issued a
          {' '}game-status report since. Nothing is blocked; check whether he is still out.
        </div>
      )}
      {illegalIrOccupants.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm space-y-2">
          <div className="text-foreground">
            <span className="font-semibold">
              {illegalIrOccupants.length === 1 ? 'A recovered player is still in IR' : 'Recovered players are still in IR'}
            </span>
            {' — '}
            {illegalIrOccupants.map(p => `${p.full_name} (${p.position_slot})`).join(', ')}.
            {' '}IR slots are only for players who cannot play, so your roster is over the limit.
            {' '}Adds, waiver claims and trades stay blocked until you move
            {' '}{illegalIrOccupants.length === 1 ? 'him' : 'them'} out.
          </div>
          <div className="flex flex-wrap gap-2">
            {illegalIrOccupants.map(p => (
              <Button
                key={p.roster_id}
                size="sm"
                variant="secondary"
                className="h-7"
                disabled={lineupBusy}
                onClick={() => { void handleActivateFromIr(p); }}
              >
                Move {p.full_name} out of IR
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Select-mode banner — shown while a player is picked for a move */}
      {moveSource && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm">
          <span className="text-foreground min-w-0 truncate">
            Moving <span className="font-semibold">{moveSource.full_name}</span> ({moveSource.position}) — tap a highlighted slot badge to move or swap.
          </span>
          <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={handleCancelMove} disabled={lineupBusy}>Cancel</Button>
        </div>
      )}

      {pastWeek ? (
        // A completed week: the lineup as scored, read-only. Never today's roster under a past
        // week's heading -- that was the bug.
        <NFLPastWeekLineup data={pastWeek} week={selectedWeek} onOpenPlayer={setDetailPlayerId} />
      ) : (<>
      {/* Starters Table */}
      <Card className="bg-card border-border overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center justify-between">
            <span className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Starters
            </span>
            <span className="text-[10px] text-muted-foreground font-normal">
              {STARTER_SLOTS.filter(s => slotMap[s.slot]).length}/{STARTER_SLOTS.length} filled
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          {/* Table header */}
          {/* SAB-1176: every stat/score header is a sort button.
              BREAKPOINT, measured not guessed: at 880px the row's fixed children already
              total 806 of 824px (slot 45 + actions 59 + TRS 60 + ANT 60 + 6x52 + PROJ 63 +
              SAB 72, plus 108 of gaps and 27 of padding), which crushed the PLAYER cell to
              19px -- the name rendered as a sliver. The handoff's column table does not
              account for this page's existing PROJ column or its lineup action cluster,
              which together are ~122px it never budgeted for.
              So the six stat columns appear at `2xl`: at a 1280 viewport the roster content
              column is only 952px, and 276px of stat columns cannot coexist there with the
              name floor, PROJ, SAB, TRS/ANT and the action cluster. Measured, not guessed.
              and PLAYER carries a hard min-width floor. Without the floor the cell is the
              only shrinkable sibling and collapses instead of forcing a scrollbar -- the
              page passes `scrollWidth === clientWidth` while being unreadable, which is
              exactly the failure the handoff records for the Players surface. The PLAYER cell is the only
              flexible one (flex-1 min-w-0 + truncate); every other column is fixed-width, which
              is what keeps the grid from ever scrolling sideways. The six stat columns are
              hidden below `sm` -- mobile gets the card layout instead of a column grid. */}
          <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider border-b border-border/40">
            <span className="w-8 sm:w-10 shrink-0 text-muted-foreground">Slot</span>
            <span className="flex-1 min-w-0 sm:min-w-[160px] xl:min-w-[190px] text-muted-foreground">Player</span>
            {isOwnTeam && <span className="w-12 text-center shrink-0 hidden sm:block"></span>}
            {/* SAB-1199: PLAIN LABELS. These were sort buttons; My Team no longer sorts.
                Same widths, tones and breakpoints as before -- only the affordance is gone,
                matching the Slot / Player / PROJ spans that were always plain. */}
            {/* The score column shows whichever metric the TOT/TRS/ANT toggle selected
                (SAB-1199), so its heading must say which. A fixed "SAB PTS" put ANT values
                under a SAB PTS heading whenever the toggle was on ANT. */}
            {/* Phones: PROJ stacks UNDER the score heading (and its value under the score in
                each row) instead of taking its own 54px column -- the widest single change
                to the name row. The score heading's own text is untouched. */}
            <div className="shrink-0 w-14 sm:w-16 flex flex-col items-end">
              <span data-col="score" className={cn(HEAD_CLASS, 'font-bold', SCORE_HEAD[statsFilter].tone)}>
                {SCORE_HEAD[statsFilter].label}
              </span>
              <span aria-hidden className="sm:hidden text-right text-muted-foreground">PROJ</span>
            </div>
            <span data-col="trs" className={cn(HEAD_CLASS, 'hidden sm:block w-[60px] text-damage')}>TRS</span>
            <span data-col="ant" className={cn(HEAD_CLASS, 'hidden sm:block w-[60px] text-success')}>ANT</span>
            {MY_TEAM_STAT_COLUMNS.map(c => (
              <span key={c.key} data-col={c.key} className={cn(HEAD_CLASS, 'hidden xl:block', c.width, TONE_CLASS[c.tone])}>
                {c.label}
              </span>
            ))}
            {/* SAB-994: weekly projection reads "expected, then got" -- kept last so the
                redesign's stat columns do not separate it from the score it explains. */}
            <span data-col="proj" className="hidden sm:block sm:w-14 text-right shrink-0 text-muted-foreground">PROJ</span>
          </div>

          {STARTER_SLOTS.map(({ slot, label }) => {
            const player = slotMap[slot] || null;
            return (
              <RosterRow
                key={slot}
                slot={slot}
                label={label}
                player={player}
                score={player ? getScore(player) : null}
                formatScore={formatScore}
                statRow={statRowFor(player)}
                view={view}
                statsPending={statsPending}
                statsFilter={statsFilter}
                isOwnTeam={isOwnTeam}
                onPlayerClick={setDetailPlayerId}
                onOpenNews={(pid) => { setDetailTab('news'); setDetailPlayerId(pid); }}
                onDrop={handleDrop}
                onMoveIr={handleMoveIr}
                onStartMove={handleStartMove}
                onCancelMove={handleCancelMove}
                onMoveToSlot={handleMoveToSlot}
                isMoveSource={!!player && moveSource?.roster_id === player.roster_id}
                isMoveTarget={moveTargetSlots.has(slot)}
                isSwapTarget={!!player && swapTargetSlots.has(slot)}
                onSwapWith={handleSwap}
                selecting={!!moveSource}
                busy={lineupBusy}
                pending={pendingSlots.includes(slot)}
                isBlockedSwap={blockedSwapSlots.has(slot)}
                onBlockedSwap={handleBlockedSwap}
              />
            );
          })}
        </CardContent>
      </Card>

      {/* Bench Table */}
      <Card className="bg-card border-border overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center justify-between">
            <span>Bench</span>
            <span className="text-[10px] text-muted-foreground font-normal">
              {BENCH_SLOTS.filter(s => slotMap[s.slot]).length}/{BENCH_SLOTS.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          {BENCH_SLOTS.map(({ slot, label }) => {
            const player = slotMap[slot] || null;
            return (
              <RosterRow
                key={slot}
                slot={slot}
                label={label}
                player={player}
                score={player ? getScore(player) : null}
                formatScore={formatScore}
                statRow={statRowFor(player)}
                view={view}
                statsPending={statsPending}
                statsFilter={statsFilter}
                isOwnTeam={isOwnTeam}
                onPlayerClick={setDetailPlayerId}
                onOpenNews={(pid) => { setDetailTab('news'); setDetailPlayerId(pid); }}
                onDrop={handleDrop}
                onMoveIr={handleMoveIr}
                onStartMove={handleStartMove}
                onCancelMove={handleCancelMove}
                onMoveToSlot={handleMoveToSlot}
                isMoveSource={!!player && moveSource?.roster_id === player.roster_id}
                isMoveTarget={moveTargetSlots.has(slot)}
                isSwapTarget={!!player && swapTargetSlots.has(slot)}
                onSwapWith={handleSwap}
                selecting={!!moveSource}
                busy={lineupBusy}
                pending={pendingSlots.includes(slot)}
                isBlockedSwap={blockedSwapSlots.has(slot)}
                onBlockedSwap={handleBlockedSwap}
              />
            );
          })}
        </CardContent>
      </Card>

      {/* IR */}
      <Card className="bg-card border-border overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Badge variant="outline" className="border-red-800/30 text-red-400 text-[10px] px-1.5">IR</Badge>
              Injured Reserve
            </span>
            <span className="text-[10px] text-muted-foreground font-normal">
              {IR_SLOTS.filter(s => slotMap[s.slot]).length}/{IR_SLOTS.length}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          {IR_SLOTS.map(({ slot, label }) => {
            const player = slotMap[slot] || null;
            return (
              <RosterRow
                key={slot}
                slot={slot}
                label={label}
                player={player}
                score={player ? getScore(player) : null}
                formatScore={formatScore}
                statRow={statRowFor(player)}
                view={view}
                statsPending={statsPending}
                statsFilter={statsFilter}
                isOwnTeam={isOwnTeam}
                onPlayerClick={setDetailPlayerId}
                onOpenNews={(pid) => { setDetailTab('news'); setDetailPlayerId(pid); }}
                onDrop={handleDrop}
                onMoveIr={handleMoveIr}
                onStartMove={handleStartMove}
                onCancelMove={handleCancelMove}
                onMoveToSlot={handleMoveToSlot}
                isMoveSource={!!player && moveSource?.roster_id === player.roster_id}
                isMoveTarget={moveTargetSlots.has(slot)}
                isSwapTarget={!!player && swapTargetSlots.has(slot)}
                onSwapWith={handleSwap}
                selecting={!!moveSource}
                busy={lineupBusy}
                pending={pendingSlots.includes(slot)}
                isBlockedSwap={blockedSwapSlots.has(slot)}
                onBlockedSwap={handleBlockedSwap}
              />
            );
          })}
        </CardContent>
      </Card>
      </>)}

      {/* Player Detail Modal */}
        </TabsContent>

        <TabsContent value="trades" className="mt-4">
          <NFLTradePage initialTeamId={initialTradeTeamId} initialPlayerId={initialTradePlayerId} />
        </TabsContent>
      </Tabs>

      <NFLPlayerDetailModal
        onTrade={onTradeWith ? (teamId, pid) => onTradeWith(teamId, pid) : undefined}
        playerId={detailPlayerId}
        initialTab={detailTab}
        open={!!detailPlayerId}
        onOpenChange={(open) => { if (!open) { setDetailPlayerId(null); setDetailTab(undefined); } }}
        onNavigateToTrades={() => setActiveTab('trades')}
        lineup={isOwnTeam && detailRosterPlayer ? {
          rosterPlayer: detailRosterPlayer,
          irEligible: detailIrEligible,
          selecting: !!moveSource,
          busy: lineupBusy,
          onStartMove: handleStartMove,
          onMoveIr: handleMoveIr,
          onDrop: handleDrop,
        } : undefined}
      />
    </div>
    </ActiveNewsProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  Roster Row — MLB-style with headshot, position badge, actions      */
/* ------------------------------------------------------------------ */

// SAB-1015: was a private copy here. Shared now, because the matchup rows need the same
// form and a helper only this file can reach is how the two surfaces drifted apart.
const shortName = shortPlayerName;

/** Format kickoff time as "Thu 8:15 PM" */
function formatKickoff(isoStr: string | null): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const day = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
  return `${day} ${time}`;
}

/**
 * A labelled segmented control. `size="touch"` because index.css mandates 44px minimum on
 * mobile controls and the default Seg is a 34px compact row.
 */
function LabeledSeg({
  label, options, value, onChange,
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-none">
      <span className="hidden shrink-0 text-[10px] font-bold uppercase tracking-wider text-muted-foreground sm:block">
        {label}
      </span>
      <Seg
        ariaLabel={label}
        options={options}
        value={value}
        onChange={onChange}
        size="touch"
        variant="filled"
        className="sm:w-auto"
      />
    </div>
  );
}

/**
 * A sortable column header.
 *
 * A header is a real <button>; non-sortable columns stay static markup rather than disabled
 * buttons, because a tappable control that does nothing reads as broken on mobile.
 */
// Exported for NFLMyTeamPage.rosterrow.test.tsx — the badge≠row distinction is behavioural and
// invisible in code review, so it is asserted directly against this component.
export function RosterRow({
  slot,
  label,
  player,
  score,
  formatScore,
  isOwnTeam,
  onPlayerClick,
  onOpenNews,
  onDrop,
  onMoveIr,
  onStartMove,
  onCancelMove,
  onMoveToSlot,
  onSwapWith,
  isMoveSource,
  isMoveTarget,
  isSwapTarget,
  selecting,
  busy,
  pending,
  isBlockedSwap,
  onBlockedSwap,
  statRow,
  view,
  statsPending,
  statsFilter,
}: {
  slot: string;
  label: string;
  player: RosterPlayer | null;
  score: number | null;
  formatScore: (val: number | null) => string;
  isOwnTeam: boolean;
  onPlayerClick?: (id: string) => void;
  /** SAB-1172: open this player's card on the News tab. */
  onOpenNews?: (playerId: string) => void;
  onDrop: (p: RosterPlayer) => void;
  onMoveIr: (p: RosterPlayer) => void;
  onStartMove: (p: RosterPlayer) => void;
  onCancelMove: () => void;
  onMoveToSlot: (slot: string) => void;
  onSwapWith: (p: RosterPlayer) => void;
  isMoveSource: boolean;
  isMoveTarget: boolean;
  isSwapTarget: boolean;
  selecting: boolean;
  busy: boolean;
  // This slot is mid-write (edge call + in-place refetch): disable its actions so a second move
  // can't be fired against stale roster state. Only affected slots get this; the page stays live.
  pending: boolean;
  // Occupied slot the source could start in, but a reciprocal swap is invalid (occupant can't take
  // the source's slot). Rendered blocked-with-a-reason (not dimmed); tap explains the two-step.
  isBlockedSwap: boolean;
  onBlockedSwap: (slot: string) => void;
  /** SAB-1176: this player's numbers for the active range. null = no scored row. */
  statRow: NflStatRow | null;
  view: NflStatView;
  statsPending: boolean;
  /** SAB-1199: which metric the TOT/TRS/ANT toggle selected. */
  statsFilter: StatsFilter;
}) {
  // Injured players eligible to be stashed on IR. The SERVER is the gate (nfl-lineup
  // handleMoveToIR); this only decides whether the button is drawn. SAB-965 changed both to
  // the same shared rule -- they used to be two copies of a list that admitted Doubtful and
  // matched no player at all, because it read `injury_status` and nothing populates it.
  //
  // SAB-1215: drawn from the SERVER's irAuthority, not isIREligible. isIREligible knows
  // nothing about the RotoWire arm SAB-1203 added to move_ir, so for two days the players
  // that path was built for never got this button (0 move_ir calls).
  const irEligible = !!player && !slot.startsWith('IR') && !!player.irAuthority;
  // SAB-1199: one source for both the digits and the tone of the SAB PTS cell.
  //
  // The TOT/TRS/ANT toggle chooses WHICH METRIC this cell shows; RANGE chooses the WINDOW.
  // They are orthogonal, so the cell reads the selected metric off the range-scoped row.
  // Falling back to `score` (week-scoped, via getScore) keeps the cell populated before the
  // stat query resolves, and keeps SAB-809's three-term-sum guard exercisable.
  const rangeMetric: number | null = statRow
    ? (statsFilter === 'trash' ? statRow.trash : statsFilter === 'anti' ? statRow.anti : statRow.sab)
    : null;
  const sabTone: number | null = rangeMetric ?? score;

  return (
    <div
      // Desktop-only native drag (HTML5 drag doesn't fire on touch — mobile uses the badge tap
      // below). Dragging reuses the SAME select-mode state as tap: onDragStart picks the source
      // (highlighting eligible targets), dropping on a highlighted row/slot completes it.
      draggable={!!player && isOwnTeam && !player.isLocked && !player.isBye && !busy && !pending}
      onDragStart={(player && isOwnTeam && !player.isLocked && !player.isBye && !pending)
        ? (e) => { e.dataTransfer.effectAllowed = 'move'; onStartMove(player); }
        : undefined}
      onDragOver={(isMoveTarget || isSwapTarget)
        ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }
        : undefined}
      onDrop={(isMoveTarget || isSwapTarget)
        ? (e) => { e.preventDefault(); if (isSwapTarget && player) onSwapWith(player); else if (isMoveTarget) onMoveToSlot(slot); }
        : undefined}
      onDragEnd={() => onCancelMove()}
      // A stable hook for the row ROOT. The behavioural tests used to reach it with
      // `badge.parentElement`, which broke the moment the row gained an inner wrapper for the
      // full-width stat line. Depth is an implementation detail; the row's identity is not.
      data-slot-row={slot}
      className={cn(
        'flex flex-col py-2.5 px-3 border-b border-border/30 last:border-0 transition-colors',
        player ? 'hover:bg-muted/20 cursor-pointer' : '',
        player?.isBye ? 'opacity-60' : player?.isLocked ? 'opacity-80' : '',
        isMoveSource ? 'ring-2 ring-inset ring-primary bg-primary/5' : '',
        (isMoveTarget || isSwapTarget) ? 'bg-primary/10 ring-1 ring-inset ring-primary/40' : '',
        // In select-mode, dim rows that are neither the source nor a valid target so targets stand out.
        // A blocked-with-a-reason slot is NOT dimmed like an ordinary ineligible one — it reads as
        // almost-valid (amber) and is tappable for the explanation.
        (selecting && !isMoveSource && !isMoveTarget && !isSwapTarget && !isBlockedSwap) ? 'opacity-30' : '',
        isBlockedSwap ? 'bg-amber-500/5 ring-1 ring-inset ring-amber-500/30' : '',
        // Mid-write: this slot is being refetched in place. Dim + pulse so it reads as pending.
        pending ? 'opacity-60 animate-pulse' : '',
      )}
      onClick={() => {
        if (busy || pending) return;
        // The row body opens the player card and NOTHING else — lineup actions live on the slot
        // badge (below). Exception: an EMPTY slot has no card, so in select-mode its whole row is
        // the move target (there is nothing else to tap there).
        if (!player) { if (isMoveTarget) onMoveToSlot(slot); return; }
        onPlayerClick?.(player.player_id);
      }}
    >
      {/* Phones: 6.75px gaps (gap-1.5) instead of 9 -- ~7px back to the name row, measured. */}
      <div className="flex w-full items-center gap-1.5 sm:gap-2">
      {/* Slot Badge — the lineup ACTION surface, kept deliberately distinct from the row body:
          tap it to Move this player; tap an eligible slot's badge to complete. The row body only
          opens the card. This is the badge≠row distinction the interaction was reworked around. */}
      {player && isOwnTeam ? (
        <button
          type="button"
          draggable={false}
          disabled={busy || pending || (player.isLocked && !selecting)}
          onClick={(e) => {
            e.stopPropagation();
            if (busy || pending) return;
            if (isSwapTarget) { onSwapWith(player); return; }   // complete a swap onto this slot
            if (isMoveSource) { onCancelMove(); return; }        // tap the source again to cancel
            if (isBlockedSwap) { onBlockedSwap(slot); return; }  // explain the two-step, no write
            if (selecting) return;                                // ineligible target mid-select: no-op
            if (player.isLocked) return;
            onStartMove(player);                                  // default: start moving this player
          }}
          title={player.isLocked ? lockLabel(player.lockReason) : isMoveSource ? 'Cancel move' : isSwapTarget ? 'Swap here' : isBlockedSwap ? `Bench ${player.full_name} to move here` : 'Move'}
          // Phones: 36px wide (w-8) instead of the 44px the global button minimum forced
          // (index.css) -- that minimum is exempted here with data-compact, and the TAP TARGET
          // is kept at 44x44 by the invisible ::after below, which takes no layout width. This
          // badge is the Move target, so its hit area is not negotiable; its drawn width is.
          data-compact
          className={cn(
            'relative w-8 sm:w-10 h-7 rounded text-[10px] font-bold flex items-center justify-center border shrink-0 transition-all',
            "after:absolute after:left-1/2 after:top-1/2 after:size-[44px] after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
            getSlotColor(slot),
            isMoveSource ? 'ring-2 ring-primary' : '',
            isSwapTarget ? 'ring-2 ring-primary animate-pulse' : '',
            isBlockedSwap ? 'ring-2 ring-amber-500/70 cursor-help' : '',
            !player.isLocked && !selecting ? 'hover:ring-2 hover:ring-primary/60 cursor-pointer' : '',
            player.isLocked ? 'opacity-50 cursor-not-allowed' : '',
          )}
        >
          {label}
        </button>
      ) : (
        <div className={cn(
          'w-8 sm:w-10 h-7 rounded text-[10px] font-bold flex items-center justify-center border shrink-0',
          getSlotColor(slot),
          isMoveTarget ? 'ring-2 ring-primary animate-pulse' : '',
        )}>
          {label}
        </div>
      )}

      {/* Player Info */}
      {player ? (
        <div className="flex-1 min-w-0 sm:min-w-[160px] xl:min-w-[190px] flex items-center gap-1.5 sm:gap-2">
          {/* Headshot */}
          <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-muted/50 overflow-hidden shrink-0 border border-border/40 relative">
            {player.headshot_url ? (
              <img
                src={player.headshot_url}
                alt={player.full_name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-[10px] text-muted-foreground font-bold">
                {player.position}
              </div>
            )}
            {/* Lock overlay on headshot */}
            {player.isLocked && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                <Lock className="h-3 w-3 text-yellow-400" />
              </div>
            )}
          </div>

          {/*
            SAB-1003 / SAB-994: the identity block is now NFLPlayerMeta, shared with the
            Matchup rows. It was three separate implementations -- this one, the matchup
            starter cell, and the matchup bench cell -- showing three different subsets of
            the same facts.

            SAB-964's finding is preserved INSIDE that component: the pill must read
            `status` as well as `injury_status`, or an IR player renders no designation at
            all. That was the bug, and it is why AvailabilityPill takes both.
          */}
          {/* The identity block and the stat line stack VERTICALLY. They were siblings in
              the row's `flex items-center` container, which made the stat line compete with
              NFLPlayerMeta for horizontal space instead of sitting under it -- at 390px that
              squeezed the meta line to width 0 while the page still reported
              scrollWidth === clientWidth. */}
          <NFLPlayerMeta
            className="flex-1"
            fullName={player.full_name}
            playerId={player.player_id}
            onOpenNews={onOpenNews ? () => onOpenNews(player.player_id) : undefined}
            shortName={shortName(player.full_name)}
            position={player.position}
            teamAlias={player.nfl_team_abbr}
            byeWeek={player.byeWeek}
            injuryStatus={player.injury_status}
            status={player.status}
            // The meta line renders in its own full-width row below; see there.
            metaLine={false}
          />

        </div>
      ) : (
        <div className="flex-1 min-w-0">
          {isMoveTarget
            ? <span className="text-xs font-semibold text-primary">Tap to move here →</span>
            : <span className="text-xs text-muted-foreground italic">Empty</span>}
        </div>
      )}

      {/* Desktop action cluster — hidden on mobile (mobile Moves via the slot badge above; IR/Drop
          via the player card). The explicit Move button mirrors MLB's per-row ArrowUpDown "Move". */}
      {isOwnTeam && player && (
        <div className="hidden sm:flex items-center gap-1 shrink-0">
          {/* Move: pick this player as the source (same select-mode the badge and drag use), then
              tap an eligible slot/player to move or swap. */}
          <button
            onClick={(e) => { e.stopPropagation(); onStartMove(player); }}
            disabled={player.isLocked || busy || pending}
            className={cn(
              "h-6 w-6 rounded flex items-center justify-center transition-colors",
              player.isLocked ? "text-muted-foreground/30 cursor-not-allowed"
                : isMoveSource ? "text-primary bg-primary/15"
                : "text-muted-foreground hover:text-primary hover:bg-primary/10"
            )}
            title={player.isLocked ? lockLabel(player.lockReason) : isMoveSource ? "Cancel" : "Move"}
          >
            <ArrowUpDown className="h-3 w-3" />
          </button>
          {/* Move to IR — only for injured players not already on IR */}
          {irEligible && (
            <button
              onClick={(e) => { e.stopPropagation(); onMoveIr(player); }}
              disabled={player.isLocked || busy || pending}
              className={cn(
                "h-6 px-1 rounded flex items-center justify-center transition-colors text-[9px] font-bold",
                player.isLocked ? "text-muted-foreground/30 cursor-not-allowed"
                  : "text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
              )}
              title={player.isLocked ? lockLabel(player.lockReason)
                : player.irAuthority === 'rotowire_recent'
                  ? "Move to IR — based on a RotoWire injury report, not an official designation"
                  : "Move to IR"}
            >
              {player.irAuthority === 'rotowire_recent' ? 'IR*' : 'IR'}
            </button>
          )}
          {/* Drop */}
          <button
            onClick={(e) => { e.stopPropagation(); onDrop(player); }}
            disabled={player.isLocked || busy || pending}
            className={cn(
              "h-6 w-6 rounded flex items-center justify-center transition-colors",
              player.isLocked ? "text-muted-foreground/30 cursor-not-allowed"
                : "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            )}
            title={player.isLocked ? lockLabel(player.lockReason) : "Drop"}
          >
            <UserMinus className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Score -- FIRST of the numeric cells, matching the header, which puts SAB PTS
          first. It sat LAST (after PROJ) from SAB-1176 / #560 until this fix, while the
          header put it first: every numeric column rendered one position off its heading,
          at every breakpoint. On mobile the PROJ value sat under "SAB PTS" and the score
          under "PROJ". The colours (red TRS, green ANT, bold score) made it read as right
          unless a value was checked against its heading, so it survived three PRs.
          `data-col` on every cell and heading is what NFLMyTeamPage.columnOrder.test.tsx
          compares; keep the two lists in the same order. */}
      {/* Phones: the projection stacks UNDER the score in this one column (see the header). */}
      <div className="shrink-0 w-14 sm:w-16 flex flex-col items-end">
      {/* flex on phones: a block div reserves a full body line (27px) around the inline score, which made this stacked column 47.5px and the row 4px TALLER than before. MEASURED. */}
      <div data-col="score" className="text-right flex justify-end sm:block">
        {/* The signed value actually shown, so the tone and the digits cannot disagree. */}

        {player?.isBye ? (
          <span className="text-[10px] font-bold text-amber-400">--</span>
        ) : (
          <span className={cn(
            'text-sm font-bold tabular-nums leading-tight sm:leading-5',  // tight on phones: score + stacked PROJ share one line's height
            // SAB-1199: the TONE must key on the value being DISPLAYED. It used to read
            // `score` (week-scoped) and now the cell renders statRow.sab (range-scoped);
            // leaving it would colour a positive season total green because that player's
            // WEEK happened to be negative. Same defect as the arithmetic one, in the
            // stylesheet instead of the number.
            sabTone === null ? 'text-muted-foreground'
              : sabTone > 0 ? 'text-primary'
              : sabTone < 0 ? 'text-emerald-400'
              : 'text-muted-foreground'
          )}>
            {/* SAB-1199: RANGE-SCOPED, like TRS and ANT beside it.
                This read getScore(player) -- week-scoped from nfl_player_match_scores at
                .eq('week', selectedWeek) -- while TRS/ANT read the range-scoped statRow. So
                at any range other than "this week" TRS + ANT did not sum to the number
                above them and the row contradicted itself.
                statRow.sab is accumulated as trash + anti + bonus over the SAME range, and
                getSabPtsDisplay also divides by gamesPlayed, so Per Game now applies here
                too -- previously this cell ignored the VIEW control entirely.
                getScore itself is unchanged ON PURPOSE: the week header total and the
                TOT/TRS/ANT filter both read it and are explicitly the WEEK's numbers.
                Range-scoping those would be this same defect inverted. */}
            {statRow ? getSabPtsDisplay(rangeMetric, view, statRow.gamesPlayed)
                     : formatScore(score)}
          </span>
        )}
      </div>
      <span data-testid="proj-stacked" className="sm:hidden text-xs leading-tight tabular-nums text-muted-foreground">
        {formatWeeklyProj(player?.weeklyProj)}
      </span>
      </div>

      {/* SAB-1176: TRS / ANT / the six shared stat columns.
          Hidden below their breakpoints -- mobile reads the card layout above instead of a
          column grid. Every cell is fixed-width so the row can never force the grid to
          scroll sideways; PLAYER is the only flexible cell. */}
      <span data-col="trs" className="hidden sm:block w-[60px] text-right text-xs font-semibold tabular-nums text-damage shrink-0">
        {statRow ? getSabPtsDisplay(statRow.trash, view, statRow.gamesPlayed) : statsPending ? '' : '--'}
      </span>
      <span data-col="ant" className="hidden sm:block w-[60px] text-right text-xs font-semibold tabular-nums text-success shrink-0">
        {statRow ? getSabPtsDisplay(statRow.anti, view, statRow.gamesPlayed) : statsPending ? '' : '--'}
      </span>
      {MY_TEAM_STAT_COLUMNS.map(c => (
        <span key={c.key} data-col={c.key}
              className={cn('hidden xl:block text-right text-xs tabular-nums text-foreground shrink-0', c.width)}>
          {statRow ? getStatDisplay(c.get(statRow), view, statRow.gamesPlayed, c.isGP) : statsPending ? '' : '–'}
        </span>
      ))}

      {/* SAB-994: this week's projection. '--' covers both "no projection exists" and
          "not loaded"; a real 0.0 (bye or non-ACT) is shown as 0.0 because that is a claim
          the view makes, not an absence. */}
      <div data-col="proj" className="hidden sm:block text-right shrink-0 sm:w-14">
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatWeeklyProj(player?.weeklyProj)}
        </span>
      </div>

      </div>

      {/* The meta line (position/team/bye, kickoff, opponent) spans the FULL ROW for the same
          reason the stat line below does. MEASURED: inside the player cell it got 157px at
          1280 and 1440, 91px at 390; the longest real line ("COACH · DEN (10)" beside
          "Mon 8:15 PM vs WAS") needs ~225px, so every row with an opponent truncated on
          desktop. Most rows had no opponent to truncate until the franchise fetch above
          stopped filtering to roster franchises, which is why this surfaced only now. */}
      {player && (
        <NFLPlayerMetaLine
          className="mt-0.5 w-full pl-12"
          // A locked row keeps its opponent and kickoff and APPENDS Locked. Substituting
          // blanked the opponent on every row during a slate -- when managers look.
          lockedKeepsGame
          position={player.position}
          teamAlias={player.nfl_team_abbr}
          byeWeek={player.byeWeek}
          isBye={player.isBye}
          isLocked={player.isLocked}
          game={{
            scheduled: player.kickoff,
            status: player.gameStatus,
            opponentAlias: player.opponentAlias,
            isHome: player.isHome,
          }}
        />
      )}

      {/* SAB-1176 follow-up: the stat line spans the FULL ROW, not the player cell.
          MEASURED, not guessed. Inside the cell it got 157px at >=xl -- the six stat columns
          take ~178px out of the cell there -- while a QB line needs 202 and an RB 204, so 10
          of 18 lines truncated MID-VALUE ("12/22 CMP, 143 YD, 1 TD, ..." cut before INT and
          SK). Mobile was worse: 91px, 17 of 18, including WR.

          The handoff budgets ~400px for PLAYER; this page also carries a PROJ column and a
          lineup action cluster it never accounted for (~122px), so the cell is 157 where the
          design implied 250+. Shortening the formats or dropping terms would both compete for
          scraps in a cell that is simply too small, and the handoff is explicit that this line
          must not be dropped -- it is what makes a K / P / DST / COACH row readable when the
          six shared columns are all dashes for them.

          Full width costs a taller row and nothing else. */}
      {player && statRow && (
        <div className="mt-1 flex w-full min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5 pl-12">
          {/* Full width on a phone so a QB line fits on ONE line: sharing the row with the
              TRS/ANT pills left it 144px and wrapped to two. From sm up the pills are gone
              (the columns carry them) and flex-1 is the whole row anyway. */}
          <p className="w-full min-w-0 text-[11px] text-foreground tabular-nums sm:w-auto sm:flex-1">
            {buildStatLine(player.position, statRow, view)}
          </p>
          {/* TRS/ANT ride here on mobile only -- the columns that carry them are hidden below sm. */}
          <span className="text-[11px] font-semibold tabular-nums text-damage sm:hidden">
            TRS {getSabPtsDisplay(statRow.trash, view, statRow.gamesPlayed)}
          </span>
          <span className="text-[11px] font-semibold tabular-nums text-success sm:hidden">
            ANT {getSabPtsDisplay(statRow.anti, view, statRow.gamesPlayed)}
          </span>
        </div>
      )}
    </div>
  );
}
